/*!
 * mm-server.js — /mashup-maker's road to the SERVER separation. It is not a new service: it is
 * the site's stem-split lane (Demucs on RunPod, StemSplitHandler) through its one browser
 * client (stem-split-lane.js) — the same jobs, the same per-minute price, the same daily
 * allowances, the same free runs for a Premium device without a graphics card, and the stems
 * land in «Мои генерации» like any split. No DOM here: the page paints offer() and runs start().
 *
 *   var S = MashupServer.create({ lane, ctx: function () { return audioContext; }, maxMb });
 *   S.reasonFor(off, mobile)          → the lane's free-fallback reason for this device, or null
 *   S.needs(engine)                   → one entry per DISTINCT full song still to separate: { ks:['a'|'b'|'c',…], song, sec, want:[stems] }
 *                                       (a song whose stems are already on the page — a rebuild, a third song added — is not asked for)
 *   S.identify(needs)                 → [{k, n, b}] — what each song will be uploaded AS (name, bytes): the
 *                                       identity the lane's duplicate guard matches on, for info({ files })
 *   S.offer(info, needs, reason)      → { state, n, owned, ownedKeys, free, paid, cost, balance, left, cap, store }
 *   S.start(engine, needs, offer, { signal, onPhase(i, phase, data) })
 *                                     → { a?: Promise<{stem: AudioBuffer}>, b?: … }  (feeds MashupEngine.build({ external }))
 *
 * Why a mashup needs it: separating two full songs in the browser takes a graphics card
 * (MUSION on WebGPU) and ~1.1–1.4 GB of the tab. An iPhone has neither — its Safari never
 * gets WebGPU for this model and a tab dies at 500–700 MB — so before this, two songs on a
 * phone ended at «separation is not available on this device», and a computer without WebGPU
 * waited half an hour on the processor.
 */
(function (root) {
    'use strict';

    var DEVICE_CANNOT = { webkit: 1, no_webgpu: 1, software_gpu: 1, gpu_rejected: 1, gpu_output_corrupt: 1, ep_ladder_exhausted: 1 };

    function create(o) {
        var lane = o.lane, ctxOf = o.ctx;
        /** The lane's size limit for a picked file (the page's config; the lane's info says the same). */
        function maxMb() {
            var inf = lane.lastInfo && lane.lastInfo();
            return (inf && inf.limits && inf.limits.max_src_mb) || o.maxMb || Infinity;
        }

        /**
         * The reason the lane accepts for a free run (StemSplitHandler::FALLBACK_REASONS) — only
         * when this device really cannot run the page's own separator. A site that failed to wire
         * the engine (`not_wired`, `no_worker`) is OUR fault, not the device's, and gets no reason:
         * that is a paid run like on any capable computer, never a free slot we would be handing
         * out for a bug.
         */
        function reasonFor(off, mobile) {
            if (off && DEVICE_CANNOT[off]) return 'no-webgpu-device';
            if (off === 'low_memory') return 'low-memory';
            if (!off && mobile) return 'mobile-unproven';
            return null;
        }

        function needs(E) {
            var out = [], byKey = {};
            ['a', 'b', 'c'].forEach(function (k) {
                var st = E.song(k);
                if (!st || st.isStem || !(st.buffer || st.released)) return;
                var want = E.wantStems(k), key = st.fileKey || ('slot:' + k);
                if (st.stems && want.every(function (w) { return !!st.stems[w]; })) return;
                if (byKey[key]) {
                    // the same file in both slots: ONE job, the union of what the two slots need
                    var n = byKey[key];
                    n.ks.push(k);
                    want.forEach(function (w) { if (n.want.indexOf(w) < 0) n.want.push(w); });
                    return;
                }
                byKey[key] = { ks: [k], song: st, sec: st.duration || (st.buffer && st.buffer.duration) || 0, want: want.slice() };
                out.push(byKey[key]);
            });
            return out;
        }

        /**
         * What each song goes up AS — the picked file, or the WAV built from its sound — named and
         * sized before anything is built: the lane's duplicate guard knows a separation by exactly
         * that (owner, name, bytes, mode), so this is how the page asks «is it already yours?».
         */
        function sourcePlan(st) {
            var f = st.file, maxB = maxMb() * 1048576;
            var isVideo = f && (/^video\//.test(f.type || '') || /\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/i.test(f.name || ''));
            if (f && !isVideo && f.size <= maxB) return { src: 'file', name: f.name, bytes: f.size };
            var base = String((f && f.name) || 'song').replace(/\.[^.]+$/, '');
            // sized from the song's own numbers: a phone may have let go of the sound itself (MashupEngine releaseSongs)
            var len = st.buffer ? st.buffer.length : Math.round(st.duration * (st.sampleRate || 44100)), ch = st.buffer ? st.buffer.numberOfChannels : (st.channels || 2);
            return { src: 'wav', name: base + '.wav', bytes: 44 + len * Math.min(2, ch) * 2 };
        }
        function identify(nds) {
            return nds.map(function (nd) {
                var p = sourcePlan(nd.song);
                nd.idn = { k: nd.song.fileKey || ('slot:' + nd.ks[0]), n: String(p.name).slice(0, 180), b: p.bytes };
                return nd.idn;
            });
        }

        /**
         * What the button may promise. A song this reader already had split (or has splitting —
         * info `owned`) costs nothing: the server hands its stems back, and the run asks for it
         * with `onlyIfOwned`, so a twin gone by the press is refused, never bought. The server
         * takes a free slot per request while it has one, so when only part of the rest is
         * covered the price names the DEARER song — the button says «up to», and the wallet
         * never loses more than it said.
         */
        function offer(info, all, reason) {
            if (!info || !info.enabled) return { state: 'off' };
            var n = all.length;
            if (!n) return { state: 'none' };
            var own = info.owned && typeof info.owned === 'object' ? info.owned : {}, ownedKeys = {};
            if (!all[0].idn) identify(all);
            all.forEach(function (nd) {
                nd.owned = (own[nd.idn.k] && own[nd.idn.k].state) || null;
                if (nd.owned) ownedKeys[nd.idn.k] = nd.owned;
            });
            var nds = all.filter(function (nd) { return !nd.owned; }), mine = n - nds.length;
            if (!nds.length) {
                var live = all.some(function (nd) { return nd.owned === 'in_flight'; });
                return { state: 'owned', n: n, owned: mine, ownedKeys: ownedKeys, live: live, free: 0, paid: 0, cost: 0, acceptPaid: false };
            }
            var quotes = nds.map(function (nd) { return lane.quote(nd.sec, info) || 0; });
            var total = quotes.reduce(function (a, b) { return a + b; }, 0);
            var tooLong = nds.some(function (nd) { return info.limits && nd.sec > info.limits.max_src_seconds + 2; });
            if (tooLong) return { state: 'too_long', n: n, max: info.limits.max_src_seconds };
            if (info.login_required) return { state: 'login', n: n, cost: total };
            var fcap = info.free_fallback_cap | 0;
            var fleft = info.free_fallback_left == null ? fcap : (info.free_fallback_left | 0);
            var free = reason && fcap > 0 ? Math.min(nds.length, Math.max(0, fleft)) : 0;
            var paidN = nds.length - free;
            var cost = quotes.slice().sort(function (a, b) { return b - a; }).slice(0, paidN).reduce(function (a, b) { return a + b; }, 0);
            var res = { n: n, owned: mine, ownedKeys: ownedKeys, free: free, paid: paidN, cost: cost, balance: info.balance | 0, fcap: fcap, fleft: fleft,
                store: info.store_url || null, reason: free ? reason : null, acceptPaid: paidN > 0 };
            // a paid split meets the wallet and nothing else — no daily file count (Farid 30.09.2026)
            if (paidN > 0 && res.balance < cost) { res.state = 'insufficient'; return res; }
            res.state = 'ready';
            return res;
        }

        /** A file the lane can take: the picked file when it is audio under the lane's size limit,
         *  otherwise (a video clip, an oversized file) the decoded sound as a 16-bit WAV — a phone
         *  video is hundreds of MB of pictures the separator never looks at. */
        async function sourceFor(st, E) {
            var p = sourcePlan(st);
            if (p.src === 'file') return { file: st.file, src: 'file' };
            var sound = st.buffer || (E && E.ensureBuffer ? await E.ensureBuffer(st.key) : null);
            if (!sound) throw Object.assign(new Error('song sound missing'), { kind: 'decode' });
            return { file: new File([toWav16(sound)], p.name, { type: 'audio/wav' }), src: 'wav' };
        }

        async function decode(ab) {
            var c = ctxOf();
            return await new Promise(function (res, rej) {
                // the callback form: old WebKit returns no promise from decodeAudioData
                var p = c.decodeAudioData(ab, res, function (e) { rej(Object.assign(new Error('decode failed'), { kind: 'decode', cause: e })); });
                if (p && p.catch) p.catch(function () {});
            });
        }

        function start(E, nds, off, so) {
            so = so || {};
            var info = lane.lastInfo(), out = {}, ownedKeys = (off && off.ownedKeys) || {};
            // ownership is what the OFFER on the screen said, not a fresher look: a song that stopped
            // being yours since must not slip through as an ordinary (paid) request with no price shown
            identify(nds);
            nds.forEach(function (nd) { nd.owned = ownedKeys[nd.idn.k] || null; });
            nds.forEach(function (nd, i) {
                var ph = function (p, d) { if (so.onPhase) so.onPhase(i, p, d); };
                var p = (async function () {
                    var src = await sourceFor(nd.song, E);
                    // an owned song asks for its twin and nothing else: gone by now → refused (not_owned), never charged
                    var job = await lane.split(src.file, { info: info, signal: so.signal, onPhase: ph, onlyIfOwned: !!nd.owned,
                        fallbackReason: nd.owned ? null : (off.reason || null), acceptPaid: !nd.owned && !!off.acceptPaid });
                    job.src = src.src;
                    // download → decode one stem at a time: a phone holds ONE compressed stem in flight
                    // (a 4-min FLAC ≈ 25 MB) next to the decoded ones, not all five of them at once
                    var t0 = Date.now(), list = (job.stems || []).filter(function (s) { return nd.want.indexOf(s.stem) >= 0; });
                    var total = list.reduce(function (a, s) { return a + (s.bytes || 0); }, 0), before = 0, bufs = {};
                    ph('download', { loaded: 0, total: total });
                    for (var j = 0; j < list.length; j++) {
                        var s = list[j];
                        var got = await lane.fetchStems(list, [s.stem], { signal: so.signal, onProgress: function (l) { ph('download', { loaded: before + l, total: Math.max(total, before + l) }); } });
                        var ab = got[s.stem];
                        before += ab.byteLength;
                        bufs[s.stem] = await decode(ab);
                        got = null; ab = null;
                    }
                    job.dlMs = Date.now() - t0;
                    job.mb = Math.round(before / 1048576);
                    if (!bufs.vocals) throw Object.assign(new Error('no vocal stem'), { kind: 'split_failed' });
                    ph('done', job);
                    return bufs;
                })();
                // a rejection nobody awaits yet (B while A is still being placed) must not surface as unhandled
                p.catch(function () {});
                nd.ks.forEach(function (k) { out[k] = p; });
            });
            return out;
        }

        return { reasonFor: reasonFor, needs: needs, identify: identify, offer: offer, start: start, sourceFor: sourceFor };
    }

    /** Interleaved 16-bit PCM WAV of an AudioBuffer (stereo at most). */
    function toWav16(ab) {
        var ch = Math.min(2, ab.numberOfChannels), n = ab.length, sr = ab.sampleRate;
        var bytes = n * ch * 2, buf = new ArrayBuffer(44 + bytes), v = new DataView(buf);
        var w = function (o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
        w(0, 'RIFF'); v.setUint32(4, 36 + bytes, true); w(8, 'WAVE'); w(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, ch, true); v.setUint32(24, sr, true);
        v.setUint32(28, sr * ch * 2, true); v.setUint16(32, ch * 2, true); v.setUint16(34, 16, true);
        w(36, 'data'); v.setUint32(40, bytes, true);
        // Песня едет на наш сервер разделяться — промежуточный файл: общий
        // квантователь без шума (нетронутое бит-в-бит; docs/why/pcm16-quantize.md).
        var pcm = new Int16Array(buf, 44, n * ch), q = window.__pcm16Quantizer(false);
        for (var c = 0; c < ch; c++) q(ab.getChannelData(c), n, pcm, c, ch);
        return buf;
    }

    root.MashupServer = { create: create, toWav16: toWav16 };
})(typeof window !== 'undefined' ? window : self);
